import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PluginConfigProps } from '../../types/PluginInterface';
import { DocumentViewerConfig, SignaturePosition } from './types';
import { Document, Page } from 'react-pdf';
import AutoTranslateText from '../../components/AutoTranslateText';
import CloudFileManager from '../../components/CloudFileManager';

const DocumentViewerConfigComponent: React.FC<PluginConfigProps> = ({ config, onConfigChange }) => {
  const cfg = config as DocumentViewerConfig;
  const [activeTab, setActiveTab] = useState<'general' | 'appearance'>('general');
  const [showFileManager, setShowFileManager] = useState(false);

  // ── Signature placement modal ─────────────────────────────────────────────
  const [showPlacer, setShowPlacer] = useState(false);
  const [numPages, setNumPages]     = useState<number | null>(null);
  const [pageDetails, setPageDetails] = useState<{ originalWidth: number; originalHeight: number } | null>(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const [dragOffset, setDragOffset]   = useState({ x: 0, y: 0 });
  const previewWrapperRef = useRef<HTMLDivElement>(null);

  // Init ownerId / ownerEmail une seule fois
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return;
    if ((cfg.userId || cfg.ownerId) && cfg.ownerEmail) return;
    try {
      const u = JSON.parse(storedUser);
      const ownerId    = u?.id    || u?.userId;
      const ownerEmail = u?.email;
      if (ownerId || ownerEmail) {
        onConfigChange({
          ...config,
          ...(ownerId    ? { userId: String(ownerId) } : {}),
          ...(ownerEmail ? { ownerEmail }               : {}),
        });
      }
    } catch { /* ignore */ }
  }, []); // eslint-disable-line

  // ── Helpers ───────────────────────────────────────────────────────────────
  const isPdfUrl = (url: string) => /\.pdf(\?|#|$)/i.test(url);

  const handleUrlChange = (url: string) => {
    if (url && !isPdfUrl(url)) { alert('Seuls les fichiers PDF sont autorisés.'); return; }
    const newFileName = url.split('/').pop() || 'document';
    onConfigChange({ ...config, documentUrl: url, documentType: 'pdf', isLocalFile: false, fileName: newFileName });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) { alert('Seuls les fichiers PDF sont autorisés.'); return; }
    onConfigChange({ ...config, documentUrl: URL.createObjectURL(file), documentType: 'pdf', isLocalFile: true, fileName: file.name });
  };

  const clearFile = () => {
    if (cfg.isLocalFile && cfg.documentUrl) URL.revokeObjectURL(cfg.documentUrl);
    onConfigChange({ ...config, documentUrl: '', isLocalFile: false, fileName: '' });
  };

  // ── Signature placement ───────────────────────────────────────────────────
  const getScaleInfo = useCallback(() => {
    const canvas = previewWrapperRef.current?.querySelector('canvas');
    if (!canvas || !pageDetails?.originalWidth) return null;
    const scale = canvas.clientWidth / pageDetails.originalWidth;
    return { canvas, scale };
  }, [pageDetails]);

  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (draggingIdx !== null) return; // fin de drag, pas un clic
    const info = getScaleInfo();
    if (!info) return;
    const rect = info.canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left)  / info.scale;
    const y = (e.clientY - rect.top)   / info.scale;
    const newPos: SignaturePosition = { page: previewPage, x, y };
    onConfigChange({ ...config, signaturePositions: [...(cfg.signaturePositions || []), newPos] });
  };

  const handleDragStart = (e: React.MouseEvent, idx: number) => {
    e.preventDefault();
    e.stopPropagation();
    const info = getScaleInfo();
    if (!info) return;
    const rect = info.canvas.getBoundingClientRect();
    const pos  = cfg.signaturePositions![idx];
    setDraggingIdx(idx);
    setDragOffset({
      x: e.clientX - (rect.left + pos.x * info.scale),
      y: e.clientY - (rect.top  + pos.y * info.scale),
    });
  };

  const handleDragMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (draggingIdx === null) return;
    const info = getScaleInfo();
    if (!info) return;
    const rect = info.canvas.getBoundingClientRect();
    const newX = Math.max(0, (e.clientX - dragOffset.x - rect.left) / info.scale);
    const newY = Math.max(0, (e.clientY - dragOffset.y - rect.top)  / info.scale);
    const next = [...(cfg.signaturePositions || [])];
    next[draggingIdx] = { ...next[draggingIdx], x: newX, y: newY };
    onConfigChange({ ...config, signaturePositions: next });
  };

  const handleDragEnd = () => setDraggingIdx(null);

  const handleRemove = (idx: number) => {
    onConfigChange({ ...config, signaturePositions: (cfg.signaturePositions || []).filter((_, i) => i !== idx) });
  };

  // ── Signature placement portal ────────────────────────────────────────────
  const renderPlacer = () => createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 999999, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ background: '#1e293b', color: 'white', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, borderBottom: '1px solid #334155' }}>
        <span style={{ fontWeight: 700, fontSize: 14 }}>📌 Placement des zones de signature</span>
        <span style={{ fontSize: 12, color: '#94a3b8', marginRight: 'auto' }}>
          {cfg.signaturePositions?.length || 0} zone(s) placée(s)
        </span>

        {/* Navigation pages */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            onClick={() => setPreviewPage(p => Math.max(1, p - 1))}
            disabled={previewPage <= 1}
            style={{ background: '#334155', color: 'white', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', opacity: previewPage <= 1 ? 0.4 : 1 }}
          >◀</button>
          <span style={{ fontSize: 13, minWidth: 90, textAlign: 'center', color: '#e2e8f0' }}>
            Page {previewPage} / {numPages ?? '…'}
          </span>
          <button
            onClick={() => setPreviewPage(p => Math.min(numPages || 1, p + 1))}
            disabled={previewPage >= (numPages || 1)}
            style={{ background: '#334155', color: 'white', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', opacity: previewPage >= (numPages || 1) ? 0.4 : 1 }}
          >▶</button>
        </div>

        <button
          onClick={() => setShowPlacer(false)}
          style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: 4, padding: '6px 16px', cursor: 'pointer', fontWeight: 700, marginLeft: 8 }}
        >✕ Fermer</button>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* PDF canvas zone */}
        <div style={{ flex: 1, overflow: 'auto', background: '#0f172a', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: 24 }}>
          <div
            ref={previewWrapperRef}
            style={{ position: 'relative', display: 'inline-block', cursor: draggingIdx !== null ? 'grabbing' : 'crosshair', userSelect: 'none' }}
            onClick={handleCanvasClick}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
          >
            <Document
              file={cfg.documentUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              loading={<div style={{ color: '#64748b', padding: 60, textAlign: 'center', fontSize: 14 }}>⏳ Chargement du PDF…</div>}
            >
              <Page
                pageNumber={previewPage}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                width={Math.min(window.innerWidth * 0.65 - 48, 1000)}
                onLoadSuccess={(page: any) => setPageDetails({ originalWidth: page.originalWidth, originalHeight: page.originalHeight })}
              />
            </Document>

            {/* Marqueurs */}
            {cfg.signaturePositions?.map((pos, idx) => {
              if (pos.page !== previewPage) return null;
              const canvas = previewWrapperRef.current?.querySelector('canvas');
              if (!canvas || !pageDetails?.originalWidth) return null;
              const scale = canvas.clientWidth / pageDetails.originalWidth;
              const sx = pos.x * scale;
              const sy = pos.y * scale;
              const isActive = draggingIdx === idx;
              return (
                <div
                  key={idx}
                  style={{ position: 'absolute', left: sx - 18, top: sy - 18, width: 36, height: 36, zIndex: 10, cursor: 'grab' }}
                  onMouseDown={(e) => handleDragStart(e, idx)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Cercle rouge */}
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: isActive ? 'rgba(220,38,38,1)' : 'rgba(239,68,68,0.88)',
                    border: '2.5px solid white',
                    boxShadow: isActive ? '0 0 0 3px rgba(239,68,68,0.4), 0 4px 12px rgba(0,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, color: 'white', transition: 'box-shadow 0.1s',
                  }}>✍</div>
                  {/* Bouton supprimer */}
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); handleRemove(idx); }}
                    style={{
                      position: 'absolute', top: -7, right: -7,
                      width: 20, height: 20, borderRadius: '50%',
                      background: '#1e293b', color: '#f87171',
                      border: '1.5px solid #475569', fontSize: 13,
                      cursor: 'pointer', lineHeight: 1, padding: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700,
                    }}
                  >×</button>
                  {/* Étiquette numéro */}
                  <div style={{
                    position: 'absolute', bottom: -18, left: '50%', transform: 'translateX(-50%)',
                    fontSize: 10, color: '#fca5a5', whiteSpace: 'nowrap', fontWeight: 600,
                    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                  }}>#{idx + 1}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sidebar */}
        <div style={{ width: 250, background: '#1e293b', color: 'white', display: 'flex', flexDirection: 'column', flexShrink: 0, borderLeft: '1px solid #334155' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #334155' }}>
            <p style={{ fontSize: 11, color: '#94a3b8', margin: 0, lineHeight: 1.6 }}>
              <strong style={{ color: '#e2e8f0' }}>Comment placer :</strong><br />
              • Cliquez sur le PDF → marqueur rouge<br />
              • Glissez le marqueur pour déplacer<br />
              • × pour supprimer<br />
              • Naviguez entre les pages via ◀ ▶
            </p>
          </div>

          <div style={{ flex: 1, overflow: 'auto', padding: 10 }}>
            {(!cfg.signaturePositions || cfg.signaturePositions.length === 0) && (
              <p style={{ color: '#475569', fontStyle: 'italic', fontSize: 12, textAlign: 'center', marginTop: 16 }}>
                Aucune zone définie.<br />Cliquez sur le PDF pour commencer.
              </p>
            )}
            {cfg.signaturePositions?.map((pos, idx) => (
              <div
                key={idx}
                onClick={() => setPreviewPage(pos.page)}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '7px 8px', marginBottom: 4, borderRadius: 4, cursor: 'pointer',
                  background: pos.page === previewPage ? '#334155' : '#0f172a',
                  border: `1px solid ${pos.page === previewPage ? '#475569' : '#1e293b'}`,
                  transition: 'background 0.15s',
                }}
              >
                <span style={{ fontSize: 12 }}>
                  <span style={{ color: '#ef4444', fontWeight: 700, marginRight: 4 }}>#{idx + 1}</span>
                  P.{pos.page} — ({Math.round(pos.x)}, {Math.round(pos.y)})
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); handleRemove(idx); }}
                  style={{ background: 'transparent', color: '#ef4444', border: 'none', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 2px' }}
                >×</button>
              </div>
            ))}
          </div>

          {cfg.signaturePositions && cfg.signaturePositions.length > 0 && (
            <div style={{ padding: 10, borderTop: '1px solid #334155' }}>
              <button
                onClick={() => onConfigChange({ ...config, signaturePositions: [] })}
                style={{ width: '100%', background: '#7f1d1d', color: 'white', border: 'none', padding: '7px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
              >🗑 Effacer toutes les zones</button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ background: '#0f172a', color: '#475569', padding: '6px 16px', fontSize: 11, textAlign: 'center', flexShrink: 0 }}>
        Cliquez sur le PDF pour ajouter une zone • Glissez pour repositionner • × pour supprimer
      </div>
    </div>,
    document.body
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 12 }}>
      {/* Onglets */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'general' ? 'active' : ''}`} onClick={() => setActiveTab('general')}>
            📄 Général
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'appearance' ? 'active' : ''}`} onClick={() => setActiveTab('appearance')}>
            🎨 Apparence
          </button>
        </li>
      </ul>

      {/* ── Onglet Apparence ──────────────────────────────────────────── */}
      {activeTab === 'appearance' && (
        <div>
          <div className="card mb-3 border-light bg-light">
            <div className="card-body">
              <h6 className="card-title text-muted mb-3">Largeur d'affichage</h6>
              <select className="form-select" value={cfg.containerWidth || '60%'} onChange={(e) => onConfigChange({ ...config, containerWidth: e.target.value })}>
                <option value="100%">Pleine largeur (100%)</option>
                <option value="90%">Très large (90%)</option>
                <option value="80%">Large (80%)</option>
                <option value="60%">Standard (60%)</option>
                <option value="50%">Demi-page (50%)</option>
                <option value="40%">Étroit (40%)</option>
              </select>
            </div>
          </div>

          <div className="card mb-3 border-light bg-light">
            <div className="card-body">
              <h6 className="card-title text-muted mb-3">Couleur de fond</h6>
              <div className="d-flex align-items-center gap-3">
                <input type="color" className="form-control form-control-color"
                  value={cfg.backgroundColor === 'transparent' ? '#ffffff' : (cfg.backgroundColor || '#ffffff')}
                  disabled={cfg.backgroundColor === 'transparent'}
                  onChange={(e) => onConfigChange({ ...config, backgroundColor: e.target.value })}
                />
                <div className="form-check mb-0">
                  <input className="form-check-input" type="checkbox" id="bgTransp"
                    checked={cfg.backgroundColor === 'transparent'}
                    onChange={(e) => onConfigChange({ ...config, backgroundColor: e.target.checked ? 'transparent' : '#ffffff' })}
                  />
                  <label className="form-check-label" htmlFor="bgTransp">Fond transparent</label>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-3 border-light bg-light">
            <div className="card-body">
              <h6 className="card-title text-muted mb-3">Style du titre</h6>
              <div className="row g-2 mb-3">
                <div className="col-md-6">
                  <label className="form-label small">Couleur</label>
                  <input type="color" className="form-control form-control-color w-100"
                    value={cfg.titleColor || '#212529'}
                    onChange={(e) => onConfigChange({ ...config, titleColor: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Taille</label>
                  <select className="form-select" value={cfg.titleFontSize || '1rem'} onChange={(e) => onConfigChange({ ...config, titleFontSize: e.target.value })}>
                    <option value="0.8rem">Petit</option>
                    <option value="1rem">Normal</option>
                    <option value="1.2rem">Moyen</option>
                    <option value="1.5rem">Grand</option>
                    <option value="2rem">Très grand</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label small">Police</label>
                  <select className="form-select" value={cfg.titleFontFamily || 'inherit'} onChange={(e) => onConfigChange({ ...config, titleFontFamily: e.target.value })}>
                    <option value="inherit">Par défaut</option>
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="'Helvetica Neue', Helvetica, sans-serif">Helvetica</option>
                    <option value="'Times New Roman', serif">Times New Roman</option>
                    <option value="'Courier New', monospace">Courier New</option>
                  </select>
                </div>
              </div>
              <div className="btn-group w-100">
                <button className={`btn btn-sm ${cfg.titleBold ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => onConfigChange({ ...config, titleBold: !cfg.titleBold })}>Gras</button>
                <button className={`btn btn-sm ${cfg.titleItalic ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => onConfigChange({ ...config, titleItalic: !cfg.titleItalic })}>Italique</button>
                <button className={`btn btn-sm ${cfg.titleUnderline ? 'btn-secondary' : 'btn-outline-secondary'}`} onClick={() => onConfigChange({ ...config, titleUnderline: !cfg.titleUnderline })}>Souligné</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Onglet Général ────────────────────────────────────────────── */}
      {activeTab === 'general' && (
        <div>
          <div className="mb-3">
            <label className="form-label fw-bold">
              <AutoTranslateText sourceLang="fr">Titre du document</AutoTranslateText>
            </label>
            <input type="text" className="form-control" value={cfg.title || ''}
              onChange={(e) => onConfigChange({ ...config, title: e.target.value })}
              placeholder="Titre du document" />
          </div>

          <div className="mb-3">
            <label className="form-label fw-bold">
              <AutoTranslateText sourceLang="fr">Source du document (PDF)</AutoTranslateText>
            </label>
            <div className="d-flex gap-2 mb-2">
              <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowFileManager(true)}>
                📂 <AutoTranslateText sourceLang="fr">Choisir</AutoTranslateText>
              </button>
              <input type="file" className="form-control form-control-sm" onChange={handleFileSelect} accept=".pdf" />
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearFile} disabled={!cfg.documentUrl} title="Effacer">×</button>
            </div>
            <div className="text-center my-2"><span className="badge bg-secondary">OU</span></div>
            <input type="url" className="form-control form-control-sm"
              value={cfg.isLocalFile ? '' : cfg.documentUrl || ''}
              onChange={(e) => handleUrlChange(e.target.value)}
              disabled={cfg.isLocalFile}
              placeholder="https://example.com/document.pdf" />
            {cfg.documentUrl && (
              <div className="alert alert-info small py-1 px-2 mt-2 mb-0">
                📄 {cfg.fileName || cfg.documentUrl}
              </div>
            )}
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label fw-bold small">
                <AutoTranslateText sourceLang="fr">Mode d'affichage</AutoTranslateText>
              </label>
              <select className="form-select form-select-sm"
                value={cfg.displayMode || 'responsive'}
                onChange={(e) => onConfigChange({ ...config, displayMode: e.target.value as 'fixed' | 'responsive' })}>
                <option value="responsive">Responsive</option>
                <option value="fixed">Dimensions fixes</option>
              </select>
            </div>
            {cfg.displayMode === 'fixed' && (
              <>
                <div className="col-md-3">
                  <label className="form-label small">Largeur (px)</label>
                  <input type="number" className="form-control form-control-sm" value={cfg.width || 800}
                    onChange={(e) => onConfigChange({ ...config, width: parseInt(e.target.value) || 800 })} />
                </div>
                <div className="col-md-3">
                  <label className="form-label small">Hauteur (px)</label>
                  <input type="number" className="form-control form-control-sm" value={cfg.height || 600}
                    onChange={(e) => onConfigChange({ ...config, height: parseInt(e.target.value) || 600 })} />
                </div>
              </>
            )}
          </div>

          <div className="mb-3">
            <div className="form-check form-switch mb-1">
              <input className="form-check-input" type="checkbox" id="showDl"
                checked={!!cfg.showDownloadButton}
                onChange={(e) => onConfigChange({ ...config, showDownloadButton: e.target.checked })} />
              <label className="form-check-label" htmlFor="showDl">
                <AutoTranslateText sourceLang="fr">Bouton Télécharger</AutoTranslateText>
              </label>
            </div>
            <div className="form-check form-switch mb-1">
              <input className="form-check-input" type="checkbox" id="showFs"
                checked={!!cfg.showFullscreenButton}
                onChange={(e) => onConfigChange({ ...config, showFullscreenButton: e.target.checked })} />
              <label className="form-check-label" htmlFor="showFs">
                <AutoTranslateText sourceLang="fr">Bouton Plein écran</AutoTranslateText>
              </label>
            </div>
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" id="enableSig"
                checked={!!cfg.enableSignature}
                onChange={(e) => onConfigChange({ ...config, enableSignature: e.target.checked })} />
              <label className="form-check-label" htmlFor="enableSig">
                <AutoTranslateText sourceLang="fr">Activer la signature électronique</AutoTranslateText>
              </label>
            </div>
          </div>

          {/* Bouton d'ouverture du placer */}
          {cfg.enableSignature && cfg.documentUrl && (
            <div className="mt-3">
              <button
                type="button"
                className="btn btn-danger w-100"
                onClick={() => { setPreviewPage(1); setShowPlacer(true); }}
              >
                📌 Placer les zones de signature
                {cfg.signaturePositions && cfg.signaturePositions.length > 0 && (
                  <span className="badge bg-light text-danger ms-2">{cfg.signaturePositions.length}</span>
                )}
              </button>
              {cfg.signaturePositions && cfg.signaturePositions.length > 0 && (
                <div className="mt-2">
                  {cfg.signaturePositions.map((pos, idx) => (
                    <span key={idx} className="badge bg-secondary me-1 mb-1" style={{ cursor: 'pointer' }}
                      onClick={() => { setPreviewPage(pos.page); setShowPlacer(true); }}>
                      #{idx + 1} P.{pos.page}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Portals */}
      {showPlacer && renderPlacer()}
      {showFileManager && (
        <CloudFileManager
          isOverlay
          onClose={() => setShowFileManager(false)}
          onSelect={(url) => {
            if (!isPdfUrl(url)) { alert('Seuls les fichiers PDF sont autorisés.'); return; }
            handleUrlChange(url);
            setShowFileManager(false);
          }}
        />
      )}
    </div>
  );
};

export default DocumentViewerConfigComponent;
